import React, { FC, ReactElement, useState } from 'react';
import { Card, Divider, Grid, Typography, useMediaQuery } from '@mui/material';
import BakeasyCheckbox from '../../components/common/BakeasyCheckbox';
import BakeasyLinkButton from '../../components/buttons/BakeasyLinkButton';
import { AppContext } from '../../context/AppContext';
import { BakeasyButton } from '../../components/buttons/BakeasyButton';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/logo.png';
import orderezLogo from '../../assets/orderezlogo.jpg';
import { BakeasySelect } from '../../components/dropdowns/BakeasySelect';
import {
  findLanguageCode,
  findLanguageName,
  useLocalStorage,
} from '../../languages/LanguageUtils';
import i18n from '../../languages/languages';
import { useAppDispatch } from '../../redux_store/reduxStore';
import { authActions } from '../../redux_store/slices/authSlice';
import { showErrorSnackbar } from '../../redux_store/slices/snackbarSlice';
import BakeasyTextFieldUsername from '../../components/textfields/BakeasyTextFieldUsername';
import BakeasyTextFieldPassword from '../../components/textfields/BakeasyTextFieldPassword';
import {
  setLoginInfo,
  clearLoginInfo,
  getLoginInfo,
} from '../../utils/LocalStorageUtil';
import env from '../../config/env';
import { validateField } from '../../utils/ValidationUtils';
import * as Regex from '../../constants/Regex';
import { LANGUAGES } from '../../constants/Constants';

export const Login: FC = (): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  let myContext = React.useContext(AppContext);
  const isMobile = useMediaQuery('(max-width:600px)');

  const [emailId, setEmailId] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [remember, setRemember] = useState(false);
  const [defaultLanguage, setDefaultLanguage] = useLocalStorage(
    'language',
    'en'
  );

  const checkValidCredentials = (): boolean => {
    if (!validateField(dispatch, 'USERNAME', emailId, Regex.username)) {
      return false;
    }
    if (!validateField(dispatch, 'PASSWORD', password)) {
      return false;
    }
    return true;
  };

  async function onSubmit(username: any, password: any) {
    try {
      if (checkValidCredentials()) {
        await dispatch(authActions.getUser({ userName: username, password }));
        if (remember) {
          setLoginInfo(username, password, remember);
        } else {
          clearLoginInfo();
        }
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
    }
  }

  const setUserDetails = (event: any): void => {
    event.preventDefault();
    if (event.target.name === 'username') {
      setEmailId(event.target.value);
    } else if (event.target.name === 'password') {
      setPassword(event.target.value);
    }
  };

  function changeLanguage(language: any) {
    setDefaultLanguage(language);
    window.location.reload();
  }

  React.useEffect(() => {
    i18n.changeLanguage(defaultLanguage);
  }, [defaultLanguage]);

  React.useEffect(() => {
    const savedLocalStorage = getLoginInfo();
    if (
      savedLocalStorage &&
      !emailId &&
      !password &&
      savedLocalStorage.remember
    ) {
      setEmailId(savedLocalStorage.username);
      setPassword(savedLocalStorage.password);
      setRemember(savedLocalStorage.remember);
    }
  }, [emailId, password]);

  return (
    <Grid>
      <Grid container sx={{ justifyContent: 'space-between' }}>
        <img src={orderezLogo} style={{ width: '100px', margin: 1 }} />
        <Grid margin={2}>
          <BakeasySelect
            label="Languages"
            statusOptions={LANGUAGES}
            value={findLanguageName(defaultLanguage)}
            size={'small'}
            onChange={(e: any) => {
              changeLanguage(findLanguageCode(e.target.value));
            }}
          />
        </Grid>
      </Grid>
      <Grid container alignItems="center" justifyContent="center" height="75vh">
        <Card
          sx={{
            margin: 1,
            padding: 2,
            borderRadius: 1,
            width: isMobile ? 'auto' : '400px',
          }}
        >
          <Grid container justifyContent="center" padding={1}>
            <Typography
              sx={{ fontWeight: 'bold', fontSize: '1.25rem', marginBottom: 4 }}
            >
              {t('SIGN_IN_PORTAL')}
            </Typography>

            <BakeasyTextFieldUsername
              required={true}
              value={emailId}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setUserDetails(event);
              }}
            />

            <Divider sx={{ margin: 1 }} />

            <BakeasyTextFieldPassword
              required={true}
              value={password}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setUserDetails(event);
              }}
            />

            <Grid
              container
              justifyContent="space-between"
              marginBottom={2}
              marginTop={2}
            >
              <BakeasyCheckbox
                label={t('REMEMBER_ME')}
                value={remember}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  setRemember(!remember);
                }}
              />

              <BakeasyLinkButton
                label={t('FORGOT_PASSOWRD')}
                style={{}}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  myContext.setConfirmDialog({
                    type: 'forgotPassword',
                    open: true,
                    title: t('FORGOT_PASSOWRD'),
                    message: t('CONTACT_SUPPORT'),
                    onYesClick: (event: any, param: string | undefined) => {},
                  });
                }}
              />
            </Grid>

            <BakeasyButton
              fullWidth={true}
              label={t('SIGN_IN')}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                onSubmit(emailId, password);
              }}
            />
            <Grid container marginTop={2} justifyContent="flex-end">
              <Typography fontSize="0.7rem">
                {t('VERSION')}: {env.VERSION}
              </Typography>
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
};
